// Figma "Карта заявок": pickup location → order → job.
import React, { useMemo, useCallback } from "react";
import { ConnectProto } from "@racoon/api";
import {
  DataTable,
  GROUPED_TABLE_CLASS_NAME,
  GroupChevron,
  GroupHeaderRow,
  useGroupedRows,
  Hyperlink,
} from "@racoon/ui";
import { getEnumName, isUndefinedEnum } from "@racoon/lib";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { Namespace, useT } from "@racoon/i18n";
import type { Row } from "@racoon/ui";
import { OrderRow } from "../../model/adapters";
import { groupJobMap, isLocationHeader, jobMapRowKey, jobMapRows } from "../../model/job-map-rows";
import { useJobMapColumns } from "../../model/use-job-map-columns";
import { useLazyGetRouteQuery } from "~/shared/api/routes/route.grpc";
import { useRouteJobsModal } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { OrderJobsTable } from "./order-jobs-table";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { OrganizationLabel } from "./organization-label";

interface JobMapTableProps {
  serviceProviderId: string;
  /** The jobs loaded so far; the table files them under pickup location → order. */
  jobs: ConnectProto.Job[];
  isFetching?: boolean;
  /** Loads the next page; absent once there is none. */
  onEndReached?: () => void;
  /** Opens the job in the edit dialog — same link the registry's id column uses */
  onOrderIdClick?: (row: OrderRow) => void;
}

// Sorting would reorder only the pages loaded so far, as in the registry.
const NO_SORTING: never[] = [];

// An order's row is the top of its card; open, its jobs' table below closes the card.
// The card sits 8px inside its location's. A table row can't be inset, so each cell
// paints its part of the card on a ::before, the outer cells 8px in from their edge.
// The row itself stays transparent, hovered or not — TableRow's own hover background
// would otherwise fill the gutters beside the card.
const ORDER_ROW = [
  "cursor-pointer !bg-transparent hover:!bg-transparent",
  "[&>td]:relative [&>td]:isolate [&>td]:bg-transparent [&>td]:px-1.5 [&>td]:py-2",
  "[&>td]:before:absolute [&>td]:before:inset-0 [&>td]:before:-z-10",
  "[&>td]:before:border-y [&>td]:before:border-slate-200 [&>td]:before:bg-white",
  "[&:hover>td]:before:bg-slate-50",
  "[&>td:first-child]:pl-4 [&>td:first-child]:before:left-2 [&>td:first-child]:before:border-l",
  "[&>td:first-child]:before:rounded-tl-xl [&>td:first-child]:before:!bg-slate-200",
  "[&>td:last-child]:pr-4 [&>td:last-child]:before:right-2 [&>td:last-child]:before:border-r",
  "[&>td:last-child]:before:rounded-tr-xl",
].join(" ");
const CLOSED_ORDER_ROW = `${ORDER_ROW} [&>td:first-child]:before:rounded-bl-xl [&>td:last-child]:before:rounded-br-xl`;

// 6px beside each header label and cell rather than 8px, so the columns fit 1440px.
const MAP_TABLE_CLASS_NAME = `${GROUPED_TABLE_CLASS_NAME} [&_th_span]:px-1.5`;

export const JobMapTable: React.FC<JobMapTableProps> = ({
  serviceProviderId,
  jobs,
  isFetching = false,
  onEndReached,
  onOrderIdClick,
}) => {
  const t = useT();

  const { columnConfig, handleColumnConfigChange } = useTableSettings("job-map", serviceProviderId);
  // Every order's jobs table shares one saved column order.
  const jobsTable = useTableSettings("job-map-jobs", serviceProviderId);

  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: serviceProviderId ? Number(serviceProviderId) : undefined,
  });
  const { openLocation, pickupLocationCard } = usePickupLocationCard();
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");

  const { activeCompany } = useAuthSession();
  const [fetchRoute] = useLazyGetRouteQuery();
  const { openRouteJobs } = useRouteJobsModal();
  const handleRouteClick = useCallback(
    (routeId: bigint) => {
      // Without unwrap the lazy query resolves either way; a failure is reported by the
      // global error toaster.
      fetchRoute({ id: routeId, ownerOrganizationId: BigInt(activeCompany?.id ?? 0) }).then(
        ({ data: route }) => {
          if (route) openRouteJobs(route);
        },
      );
    },
    [fetchRoute, activeCompany, openRouteJobs],
  );

  const allRows = useMemo(() => jobMapRows(groupJobMap(jobs), t), [jobs, t]);

  // An order opens on demand. A location starts open unless every order in it is serviced.
  const defaultExpanded = useCallback(
    (row: OrderRow) => isLocationHeader(row) && row._groupCompletedCount !== row._groupTotalCount,
    [],
  );

  const columns = useJobMapColumns({ onOrderIdClick, onCustomerClick: openCustomerCard });

  const {
    data: groupedRows,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedRows<OrderRow>({
    rows: allRows,
    columns,
    sorting: NO_SORTING,
    defaultExpanded,
  });

  // An order's card stands 8px below whatever is above it, and its row draws its own
  // chevron, so it needs its expansion on the row itself.
  const tableData = useMemo(
    () =>
      groupedRows.flatMap((row) =>
        row._mapRow === "order"
          ? [
              {
                ...row,
                _isGroupHeader: false,
                _groupDepth: undefined,
                _mapRow: "spacer" as const,
              },
              { ...row, _isExpanded: isGroupExpanded(row) },
            ]
          : [row],
      ),
    [groupedRows, isGroupExpanded],
  );

  // The location headers, the gaps above orders and the orders' jobs; an order's own row
  // is drawn with the columns, like any data row.
  const renderCustomRow = useCallback(
    (row: Row<OrderRow>, _index: number, visibleColumnCount: number) => {
      const r = row.original;
      if (r._mapRow === "spacer") {
        return (
          <tr className="h-2 border-0 bg-transparent pointer-events-none">
            <td colSpan={visibleColumnCount} className="p-0 border-0" />
          </tr>
        );
      }
      if (r._mapRow === "jobs" && r._orderJobs) {
        return (
          <tr>
            <td colSpan={visibleColumnCount} className="p-0 border-0">
              {/* The bottom of the order's card, inset like its row */}
              <div className="mx-2 overflow-hidden rounded-b-xl border-x border-b border-slate-200 bg-white">
                <OrderJobsTable
                  jobs={r._orderJobs}
                  columnConfig={jobsTable.columnConfig}
                  onColumnConfigChange={jobsTable.handleColumnConfigChange}
                  onJobIdClick={onOrderIdClick}
                  onRouteClick={handleRouteClick}
                />
              </div>
            </td>
          </tr>
        );
      }
      if (!isLocationHeader(r)) return undefined;
      const isExpanded = isGroupExpanded(r);
      const location = r.rawJob.pickupLocation;
      const typeName = getEnumName(ConnectProto.PickupLocationTypeSchema, location?.type);
      return (
        <GroupHeaderRow
          key={`group-${r._groupKey}`}
          detached
          colSpan={visibleColumnCount}
          isExpanded={isExpanded}
          onToggle={() => toggleGroup(r)}
          innerClassName="gap-4 px-4"
        >
          <GroupChevron isExpanded={isExpanded} />
          {/* The location's orders */}
          <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-lg bg-slate-200 px-1.5 text-xs font-semibold tabular-nums text-text-body">
            {r._groupTotalCount}
          </span>
          {/* The link is an inline-block: the ellipsis has to be on it, not its slot. */}
          <div className="w-[228px] min-w-0 shrink-0 text-text-body">
            <Hyperlink
              className="max-w-full truncate align-middle text-sm font-bold uppercase"
              title={r._groupLocationName}
              onOpen={() => {
                // The raw proto, not the adapted job: the card reads the
                // numeric enums the backend sent.
                if (location) openLocation(location);
              }}
            >
              {r._groupLocationName}
            </Hyperlink>
          </div>
          {/* The location's customer, then its provider */}
          <div className="w-[200px] shrink-0">
            {r.rawJob.serviceReceiver && (
              <OrganizationLabel
                organization={r.rawJob.serviceReceiver}
                // Another organization's zone job (or one not yet known as ours) opens no card
                onOpen={
                  isOwnForWrite(r.rawJob.serviceProviderId, r.rawJob.serviceReceiverId) === true
                    ? () => {
                        if (r.rawJob.serviceReceiver) openCustomerCard(r.rawJob.serviceReceiver);
                      }
                    : undefined
                }
              />
            )}
          </div>
          <div className="w-[200px] shrink-0">
            {r.rawJob.serviceProvider && (
              <OrganizationLabel organization={r.rawJob.serviceProvider} />
            )}
          </div>
          <div className="flex-1" />
          {typeName && !isUndefinedEnum(typeName) && (
            <span className="whitespace-nowrap text-sm text-text-subtle">
              {t(Namespace.Orders, "jobMap.locationType", {
                type: t(Namespace.Enum, typeName),
              })}
            </span>
          )}
        </GroupHeaderRow>
      );
    },
    [
      isGroupExpanded,
      toggleGroup,
      t,
      isOwnForWrite,
      openCustomerCard,
      openLocation,
      onOrderIdClick,
      handleRouteClick,
      jobsTable.columnConfig,
      jobsTable.handleColumnConfigChange,
    ],
  );

  // Only an order's row is drawn by the table itself.
  const rowClassName = useCallback(
    (row: OrderRow) => (row._isExpanded ? ORDER_ROW : CLOSED_ORDER_ROW),
    [],
  );

  const handleRowClick = useCallback(
    (row: OrderRow) => {
      if (row._isGroupHeader) toggleGroup(row);
    },
    [toggleGroup],
  );

  if (!serviceProviderId) return null;

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1 gap-3">
      <div className="w-full min-h-0 overflow-hidden rounded-xl border-slate-200  flex flex-col">
        <div className="flex-1 min-h-0 relative">
          <DataTable
            data={tableData}
            columns={columns}
            columnConfig={columnConfig}
            onColumnConfigChange={handleColumnConfigChange}
            namespace={Namespace.Orders}
            loading={isFetching && tableData.length === 0}
            renderCustomRow={renderCustomRow}
            rowKey={jobMapRowKey}
            zebra={false}
            onRowClick={handleRowClick}
            className={MAP_TABLE_CLASS_NAME}
            rowClassName={rowClassName}
            onEndReached={onEndReached}
            loadingMore={isFetching && tableData.length > 0}
          />
        </div>
      </div>

      {pickupLocationCard}
    </div>
  );
};
